import {
  Box,
  Flex,
  HStack,
  Icon,
  Show,
  Skeleton,
  SkeletonCircle,
  Stack,
  Text,
} from '@chakra-ui/react';
import { StateEnum } from '@halo/chat-sdk';
import { ChatAvatar, GroupIcon, RobotIcon, OfficialIcon, Tooltip } from '@halo/ui-chat-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ChatPanelHeaderProps {
  avatar: string | undefined;
  sessionName: string;
  username?: string;
  secondaryName?: string;
  state?: StateEnum;
  isGroup: boolean;
  isGroupDisband: boolean;
  isInGroup: boolean;
  memberCount?: number;
  extra?: ReactNode;
  onAvatarClick: () => void;
  connecting?: boolean;
  duration?: number;
  show?: boolean;
  isRobot?: boolean;
  tooltip?: string;
  official?: boolean;
}

function ChatPanelHeader({
  avatar,
  sessionName,
  username,
  secondaryName,
  state,
  isGroup,
  isGroupDisband,
  isInGroup,
  memberCount,
  extra,
  onAvatarClick,
  duration,
  show,
  isRobot,
  tooltip,
  official,
}: ChatPanelHeaderProps) {
  const { t } = useTranslation();
  const isGroupError = isGroup && (isGroupDisband || !isInGroup);

  return (
    <Box
      className="ChatPanelHeader"
      data-state={show ? 'open' : 'closed'}
      px="2"
      py="3"
      display="flex"
      alignItems="center"
      gap="2.5"
      borderBottom="1px solid"
      borderColor="border.muted"
      animationName={{ _open: 'fade-in', _closed: 'fade-out' }}
      animationDuration="slower"
    >
      <Show
        when={show}
        fallback={
          <Flex gap="2" w="300px" align="center">
            <SkeletonCircle size="10" />
            <Stack flex="1">
              <Skeleton height="8" width="80%" />
            </Stack>
          </Flex>
        }
      >
        <ChatAvatar
          src={avatar}
          size="sm"
          onClick={onAvatarClick}
          duration={duration}
          cursor={isGroupError ? 'default' : 'pointer'}
          showExpireIcon={!isRobot}
        />
        <Box
          className="ChatPanelHeaderMeta"
          display="flex"
          flexDirection="column"
          gap="2"
          flex="1"
          overflow="hidden"
        >
          <Box fontSize="sm" display="flex" alignItems="center" gap="1">
            {isGroup && <GroupIcon />}
            <Box
              lineHeight="normal"
              maxW="50%"
              cursor={isGroupError ? 'default' : 'pointer'}
              onClick={onAvatarClick}
            >
              <HStack gap="1">
                {isRobot && (
                  <Icon w="1em" h="1em">
                    <RobotIcon />
                  </Icon>
                )}
                <Text fontSize="sm" fontWeight="semibold" pb="0.5" truncate>
                  {isGroup
                    ? sessionName
                    : state === StateEnum.DELETE_ACCOUNT
                      ? t('chat.deleteAccountText')
                      : `${sessionName ?? ''}${secondaryName && `(${secondaryName})`}`}
                </Text>
                {isRobot && official && (
                  <Tooltip
                    showArrow={true}
                    content={tooltip}
                    openDelay={0}
                    closeDelay={0}
                    positioning={{
                      placement: 'top',
                    }}
                  >
                    <OfficialIcon width="1em" height="1em" />
                  </Tooltip>
                )}
              </HStack>
              {!isGroup && username && state !== StateEnum.DELETE_ACCOUNT && (
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  lineHeight="normal"
                  color="fg.muted"
                  truncate
                >
                  @{username}
                </Text>
              )}
            </Box>
            {isGroup && !!memberCount && (
              <Text
                fontSize="xs"
                fontWeight="medium"
                lineHeight="normal"
                color="fg.muted"
                cursor={isGroupError ? 'default' : 'pointer'}
                onClick={onAvatarClick}
              >
                {memberCount} {t('chat.groupMembersText')}
              </Text>
            )}
          </Box>
          {/* <Box
          className="ChatPanelHeaderMetaTabs"
          fontSize="xs"
          color="fg.muted"
          fontWeight="semibold"
          lineHeight="normal"
          display="flex"
          gap="2"
        >
          <Box>Chat</Box>
          <Box>Pin</Box>
          <Box>Announcements</Box>
        </Box> */}
        </Box>
        {extra}
      </Show>
    </Box>
  );
}

const MemoizedChatPanelHeader = memo(ChatPanelHeader);

export { MemoizedChatPanelHeader as ChatPanelHeader };
